import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WidgetProps } from '@rjsf/utils';
import SignaturePad from 'signature_pad';

type SignatureMode = 'type' | 'draw' | 'initials';
type SignatureValue = { mode: SignatureMode; data: string };

const CURSIVE_FONT_URL = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap';

// Inject the Google Font stylesheet once
let fontLoaded = false;
function ensureCursiveFont() {
  if (fontLoaded) return;
  fontLoaded = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = CURSIVE_FONT_URL;
  document.head.appendChild(link);
}

function parseValue(value: any): SignatureValue {
  if (!value) return { mode: 'type', data: '' };
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.mode && typeof parsed.data === 'string') return parsed;
  } catch { /* not JSON */ }
  // Legacy or plain string — treat as typed name
  if (typeof value === 'string' && value.length > 0) return { mode: 'type', data: value };
  return { mode: 'type', data: '' };
}

const SignatureWidget: React.FC<WidgetProps> = ({ id, value, onChange, readonly, disabled, schema }) => {
  ensureCursiveFont();

  const parsed = parseValue(value);
  const [mode, setMode] = useState<SignatureMode>(parsed.mode);
  const [typedName, setTypedName] = useState(parsed.mode === 'type' ? parsed.data : '');
  const [initials, setInitials] = useState(parsed.mode === 'initials' ? parsed.data : '');
  const [focused, setFocused] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const emit = useCallback((m: SignatureMode, data: string) => {
    onChange(JSON.stringify({ mode: m, data }));
  }, [onChange]);

  // Initialize signature pad when switching to draw mode
  useEffect(() => {
    if (mode !== 'draw' || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;

    // Set canvas dimensions to match container
    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = container?.clientWidth || 500;
      const height = 150;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(ratio, ratio);
    };

    resizeCanvas();

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
    });

    // Restore existing drawn signature
    if (parsed.mode === 'draw' && parsed.data) {
      try { pad.fromDataURL(parsed.data); } catch { /* ignore */ }
    }

    pad.addEventListener('endStroke', () => {
      emit('draw', pad.toDataURL('image/png'));
    });

    sigPadRef.current = pad;

    if (readonly || disabled) pad.off();

    return () => {
      pad.off();
      sigPadRef.current = null;
    };
  }, [mode]);

  const handleClear = () => {
    if (mode === 'draw' && sigPadRef.current) {
      sigPadRef.current.clear();
      emit('draw', '');
    } else if (mode === 'type') {
      setTypedName('');
      emit('type', '');
    } else if (mode === 'initials') {
      setInitials('');
      emit('initials', '');
    }
  };

  const handleModeChange = (newMode: SignatureMode) => {
    setMode(newMode);
    // Emit new mode's current data so RJSF stores the mode switch
    if (newMode === 'type') emit('type', typedName);
    else if (newMode === 'initials') emit('initials', initials);
    else if (newMode === 'draw') emit('draw', '');
  };

  const isDisabled = readonly || disabled;

  const modeButtons: { key: SignatureMode; label: string; icon: string }[] = [
    { key: 'type', label: 'Type Name', icon: 'fas fa-keyboard' },
    { key: 'draw', label: 'Draw', icon: 'fas fa-pen' },
    { key: 'initials', label: 'Initials', icon: 'fas fa-font' },
  ];

  return (
    <div id={id} ref={containerRef}>
      {/* Mode tabs */}
      <div className="d-flex gap-1 mb-2">
        {modeButtons.map((btn) => (
          <button
            key={btn.key}
            type="button"
            className={`btn btn-sm ${mode === btn.key ? 'btn-primary' : 'btn-outline btn-outline-primary'}`}
            onClick={() => handleModeChange(btn.key)}
            disabled={isDisabled}
          >
            <i className={`${btn.icon} inherit-color me-1`}></i>{btn.label}
          </button>
        ))}
      </div>

      {/* Signature area */}
      <div className="border rounded p-2" style={{ backgroundColor: '#fff', minHeight: 100 }}>
        {mode === 'type' && (
          <input
            className="form-control border-0"
            style={{ fontFamily: "'Dancing Script', cursive", fontSize: 32, lineHeight: '60px', height: 80, textAlign: 'center' }}
            placeholder={focused ? '' : 'Type your full name'}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); emit('type', typedName); }}
            disabled={isDisabled}
          />
        )}

        {mode === 'draw' && (
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: 150, cursor: isDisabled ? 'default' : 'crosshair', touchAction: 'none' }}
          />
        )}

        {mode === 'initials' && (
          <input
            className="form-control border-0"
            style={{ fontFamily: "'Dancing Script', cursive", fontSize: 40, lineHeight: '60px', height: 80, textAlign: 'center', maxWidth: 200, margin: '0 auto' }}
            placeholder={focused ? '' : 'Initials'}
            maxLength={5}
            value={initials}
            onChange={(e) => setInitials(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); emit('initials', initials); }}
            disabled={isDisabled}
          />
        )}
      </div>

      {/* Clear button */}
      {!isDisabled && (
        <div className="d-flex justify-content-end mt-1">
          <button type="button" className="btn btn-sm btn-link text-muted p-0" onClick={handleClear}>
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

/** Read-only signature display for form viewer */
export const SignatureDisplay: React.FC<{ value: any }> = ({ value }) => {
  ensureCursiveFont();
  const parsed = parseValue(value);
  if (!parsed.data) return <span className="text-muted">No signature</span>;

  if (parsed.mode === 'draw') {
    return <img src={parsed.data} alt="Signature" style={{ maxWidth: '100%', maxHeight: 150 }} />;
  }

  return (
    <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: parsed.mode === 'initials' ? 36 : 28 }}>
      {parsed.data}
    </span>
  );
};

export { SignatureWidget };
